import { HttpParams } from '@angular/common/http';

/**
 * A path below the API root whose shape was fixed in source. Only the `api` tag creates one, and
 * RestClient accepts nothing else, so no runtime value can decide which URL is requested - a value
 * can only fill one segment or one query value.
 */
export class ApiPath {
  private constructor(private readonly path: string) {}

  /** Exported as `api`. Lives here because only the class can call its private constructor. */
  static build<T extends readonly unknown[]>(
    literals: TemplateStringsArray,
    ...values: [...T] & { [K in keyof T]: Interpolable<T[K]> }
  ): ApiPath {
    let path = literals[0];
    for (let i = 0; i < values.length; i++) {
      path += ApiPath.encode(values[i] as ApiPathValue) + literals[i + 1];
    }
    return new ApiPath(path);
  }

  private static encode(value: ApiPathValue): string {
    if (value === null || value === undefined) return '';
    if (value instanceof ApiPath || value instanceof HttpParams) return value.toString();
    if (value instanceof PathPart) return encodeURIComponent(value.value);
    // Unreachable through the types; kept so an `as` cast still cannot smuggle raw text in.
    return encodeURIComponent(String(value));
  }

  toString(): string {
    return this.path;
  }
}

/** A string that has been declared, at the call site, to be one segment or one query value. */
export class PathPart {
  constructor(readonly value: string) {}
}

/**
 * Marks runtime text as one unit of a path - a segment or a query value - to be encoded whole.
 *
 * Strings must go through this rather than straight into `api`, so that every string in a path is
 * a visible decision. A prefix constant such as 'Tank/details' is not one unit; build it with `api`
 * instead and interpolate the ApiPath, which is inserted as written.
 */
export function part(value: string | number | boolean | null | undefined): PathPart {
  return new PathPart(value === null || value === undefined ? '' : String(value));
}

/** What `api` accepts. Note what is missing: a bare string. */
export type ApiPathValue = number | boolean | ApiPath | HttpParams | PathPart | null | undefined;

/**
 * Rejects `any` and `unknown` as well as string. Both would otherwise pass the ApiPathValue check -
 * `any` silently, `unknown` via an `as` - and an `any`-typed id is usually a string at runtime.
 */
type Interpolable<T> = 0 extends 1 & T ? never : unknown extends T ? never : T extends ApiPathValue ? T : never;

/**
 * Builds an ApiPath. The literal text is the path; every interpolated value is one unit within it:
 *
 *   api`${this.apiURL}/history/${tankId}?name=${part(tankName)}`
 *
 * - number, boolean: written as text. Neither can contain a path or query separator.
 * - part(text): encoded with encodeURIComponent, so it can never add a '/', '?', '&', '#', '..' or a
 *   scheme. Pass raw text - encoding it first double-encodes it.
 * - ApiPath: inserted as built, so a path can be assembled from parts that were each built with `api`.
 * - HttpParams: inserted as its own serialisation, which Angular encodes per key and value.
 * - null and undefined: inserted as the empty string rather than the text 'null' or 'undefined'.
 *
 * A bare string, `any` or `unknown` is a compile error ("not assignable to parameter of type
 * 'never'"): wrap it in part(), or convert it with Number() if it is really an id.
 */
export const api = ApiPath.build;
